import { filter, get, isNumber } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { isBusinessEntity } from '../../../../../model/OrganisationTypes';
import useFieldReset from '../../../../data/hooks/useFieldReset';
import useOrganisationGroupName from '../../../../data/hooks/useOrganisationGroupName';
import organisationStructureTreeSelector from '../../../../data/organisationStructureTreeSelector.graphql';
import withCurrentUser from '../../../../data/withCurrentUser';
import nodeSortingOptionsSequence from '../../../../utils/nodeSortingOptionsSequence';
import useT from '../../../utils/Translations/useT';
import {
  isLevel,
  isOrganisation,
  isOrganisationStructure,
  LEVEL,
  ORGANISATION,
  ORGANISATION_STRUCTURE,
} from './RoleAssignmentField/RoleAssignmentField';
import TreeSelectorField from './TreeSelectorField';

const OrganisationsTreeSelectorField = ({
  orgGroupId,
  disabled,
  currentUser,
  me,
  systemConfig,
  label,
  plugins = {},
  ...rest
}) => {
  const t = useT();
  const orgGroupName = useOrganisationGroupName(orgGroupId);

  const defaultValue = useMemo(
    () =>
      get(me, 'organisationGroupId', null) === orgGroupId
        ? get(me, 'organisations', [])
        : null,
    [orgGroupId, me],
  );

  useFieldReset(rest.name, orgGroupId);

  return (
    <TreeSelectorField
      hasSyntheticRootNode
      adapter={adapter}
      defaultValue={defaultValue}
      disabled={!isNumber(orgGroupId) || disabled}
      gql={organisationStructureTreeSelector}
      gqlSkip={!isNumber(orgGroupId)}
      gqlVariables={{ orgGroupId }}
      hasTitle={false}
      label={t(label)}
      plugins={{
        nodeSortingOptions: nodeSortingOptionsSequence,
        ...plugins,
      }}
      selectionFilter={selectionFilter}
      syntheticRootNodeName={orgGroupName}
      tooltipPosition="responsive"
      {...rest}
      clearOnTop
    />
  );
};

OrganisationsTreeSelectorField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  orgGroupId: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  hasTitle: PropTypes.bool,
  ...withCurrentUser.props,
};

OrganisationsTreeSelectorField.defaultProps = {
  name: 'organisations',
  label: 'Organisations',
  orgGroupId: null,
  required: true,
  disabled: false,
  hasTitle: false,
  ...withCurrentUser.defaultProps,
};

export default withCurrentUser()(OrganisationsTreeSelectorField);

const nodeIsLeaf = isLevel;

const resolveNodeParentId = node => {
  if (isOrganisation(node) && !node.parentId) {
    return null;
  }

  if (isOrganisation(node) && node.parentId) {
    return `${ORGANISATION}-${node.parentId}`;
  }

  if (isOrganisationStructure(node)) {
    return `${ORGANISATION}-${node.organisationId}`;
  }

  if (isLevel(node) && node.parentId === null) {
    return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.organisationStructureId}`;
  }

  if (isLevel(node) && node.parentId) {
    return `${LEVEL}-${node.organisationId}-${node.parentId}`;
  }

  return node.parentId;
};

const resolveNodeId = node => {
  if (isOrganisation(node)) {
    return `${ORGANISATION}-${node.id}`;
  }

  if (isOrganisationStructure(node)) {
    return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.id}`;
  }

  if (isLevel(node)) {
    return `${LEVEL}-${node.organisationId}-${node.id}`;
  }

  return node.id;
};

const resolveNodeClassName = node => {
  if (isBusinessEntity(node)) {
    return 'jstree-node-bold';
  }

  if (nodeIsLeaf(node)) {
    return 'jstree-node-italic';
  }

  return '';
};

export const adapter = {
  nodeIsLeaf,
  resolveNodeParentId,
  resolveNodeId,
  resolveNodeClassName,
};

export const selectionFilter = organisations => filter(organisations, isLevel);
